/**
 * The one composition every list screen uses (docs/04 §3f, punch wave 3F).
 *
 * Matt, on the Properties list with the real 83 properties in it: *"the
 * placement of these filter options, view options, export and buttons are very
 * awkward … the header across all modules should freeze in place as the list
 * below the header scrolls … the footer of the list views should be frozen,
 * locked to the bottom of the screen that shows # of items in list and previous
 * / next. The scrolling is just awkward and needs to be standardized."*
 *
 * The screens were assembled control by control as each was built — search,
 * then status, then type, then favourites, then export, then view, then page
 * size, then columns — and nobody ever laid the finished set out. This is that
 * layout, as a component rather than a convention, because a convention is what
 * the last eight controls were following.
 *
 * WHAT GOES WHERE, and it is not arbitrary:
 *
 *   * `actions` — row 1, beside the title. What LEAVES the screen: New, Export.
 *     Nothing here changes which rows you are looking at.
 *   * `filters` — row 2 left. What NARROWS the set: search, status, type, any
 *     toggle. Read left to right in decreasing generality.
 *   * `tools` — row 2 right. What changes the PRESENTATION of the same set:
 *     Table/Cards, page size, columns. Beside the table because that is what
 *     they are about (ADR-29 moved density out of here for the same reason).
 *
 * A reader can answer "will this control change my results?" from its position
 * alone, on every list in the app.
 *
 * STICKY, NOT AN INNER SCROLLER. `<main>` is the app's one vertical scrollport
 * (0013) and it stays that way: a second scrollport is precisely what Matt was
 * complaining about. The header and footer are `position: sticky` inside it.
 * The negative offsets cancel the pane's own padding — sticky insets resolve
 * past it, so a plain `top-0` parks the bar 16px down and leaves a strip of
 * scrolled rows sliding above it. Measured, and an e2e pins it.
 */

import { clsx } from 'clsx';
import { SlidersHorizontal } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { Button } from './Button.js';
import { Dialog } from './Dialog.js';
import { Pager } from './Pager.js';

/**
 * Is the viewport below the bar's breakpoint (640, Tailwind's `sm`)?
 *
 * The filters are rendered in ONE place — the bar or the sheet, never both —
 * so this has to be a real media query rather than two copies with one hidden
 * by CSS. That was the first cut, and it put two controls with the same
 * accessible name in the DOM on every list screen: bad for a screen reader,
 * ambiguous for a test, and a second React subtree holding a second copy of
 * whatever state the caller's controls keep.
 *
 * 640 is repeated here from the `sm:` classes because a media query string is
 * not something Tailwind can hand back; the two must be changed together, which
 * is why the number is named rather than inlined twice.
 */
const BAR_BREAKPOINT_PX = 640;

function useCompactHeader(): boolean {
  const [compact, setCompact] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia(`(max-width: ${String(BAR_BREAKPOINT_PX - 1)}px)`).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${String(BAR_BREAKPOINT_PX - 1)}px)`);
    const update = (): void => {
      setCompact(query.matches);
    };
    update();
    query.addEventListener('change', update);
    return () => {
      query.removeEventListener('change', update);
    };
  }, []);
  return compact;
}

export function ListScreen({
  title,
  breadcrumb,
  tabs,
  actions,
  filters,
  tools,
  activeFilterCount = 0,
  total,
  page,
  pageSize,
  noun,
  onPage,
  footerExtra,
  children,
}: {
  readonly title: string;
  /**
   * Rendered above the title, inside the frozen header. A top-level list's
   * breadcrumb is usually one crumb and mostly ceremony, but where a screen
   * already has one it stays with the title rather than scrolling away from it.
   */
  readonly breadcrumb?: ReactNode;
  /**
   * A tab strip belonging to the screen, frozen with the title.
   *
   * `/work` is a list screen whose lanes are routes rather than filters, so its
   * tabs sit here — navigation, not narrowing, which is why they are their own
   * slot and not `filters`. Freezing them matters most on the screen where the
   * whole point is moving between lanes while reading a long one.
   */
  readonly tabs?: ReactNode;
  /** Row 1, right: what leaves the screen — New, Export. */
  readonly actions?: ReactNode;
  /** Row 2, left: what narrows the set. */
  readonly filters?: ReactNode;
  /** Row 2, right: what changes how the same set is drawn. */
  readonly tools?: ReactNode;
  /**
   * How many filters are currently narrowing the set. Shown on the mobile
   * Filters button, because a collapsed control that hides an ACTIVE filter is
   * how somebody spends five minutes wondering where their rows went.
   */
  readonly activeFilterCount?: number;
  readonly total?: number | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly noun?: string | undefined;
  readonly onPage?: ((page: number) => void) | undefined;
  /**
   * Rendered beside the count. For a "Clear filters" affordance, which belongs
   * next to the number it would change rather than floating above the rows.
   */
  readonly footerExtra?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  const [sheetOpen, setSheetOpen] = useState(false);
  const compact = useCompactHeader();
  const hasBar = filters !== undefined || tools !== undefined;
  /**
   * The COUNT is the footer's reason to exist; the pager is an addition. An
   * unpaged list (the documents browser) still gets a footer saying how many
   * rows it is showing, which is the half Matt named first.
   *
   * It renders at ZERO too, and that is deliberate: "0 documents" is a fact,
   * and — found by an e2e — the footer is also where "Clear filters" lives, so
   * hiding it at zero took away the way out of a filter that matched nothing.
   * Absent only while the count is genuinely unknown, which is during load.
   */
  const showFooter = total !== undefined;
  const canPage = page !== undefined && pageSize !== undefined && onPage !== undefined;

  return (
    <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-(--stack-gap)">
      {/* ---------------------------------------------------------------- */}
      {/* Header — frozen. Both rows, not just the filter bar: a title that   */}
      {/* scrolls away takes with it the only thing telling you which list    */}
      {/* you are in, which on a long scroll is the question people ask.      */}
      {/* ---------------------------------------------------------------- */}
      {/**
       * `top-0`, not the old toolbar's `-top-4 pt-4`.
       *
       * That trick pulled the bar flush with the pane's edge once stuck — which
       * means it MOVED 16px on the first pixel of scroll, because unstuck it sat
       * below the pane's padding and stuck it sat above it. Nobody noticed while
       * it was one thin toolbar; under a whole header it is a visible jolt, and
       * "frozen" should mean the box does not move at all. The e2e asserts
       * exactly that, and it caught this.
       *
       * `top-0` puts the sticky position at the pane's CONTENT box top, which is
       * also the header's flow position as the first child — so it is already
       * there and never shifts. The `::before` paints the pane's 16px padding
       * band above it, so rows still have nothing to slide through.
       */}
      <div
        className={clsx(
          'sticky top-0 z-10 -mx-1 flex flex-col gap-2 bg-canvas px-1 pb-2',
          "before:absolute before:inset-x-0 before:-top-4 before:h-4 before:bg-canvas before:content-['']",
        )}
      >
        {breadcrumb}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-display text-ink">{title}</h1>
          <span className="flex items-center gap-2">
            {/* The filters collapse below 640 and nothing else does: a frozen
                two-row bar on a 375px screen is most of the screen, which would
                make freezing a downgrade rather than the fix Matt asked for. */}
            {hasBar && compact ? (
              <Button
                variant="secondary"
                aria-expanded={sheetOpen}
                onClick={() => {
                  setSheetOpen(true);
                }}
              >
                <SlidersHorizontal size={15} aria-hidden />
                Filters
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-brand-700 px-1.5 text-chip font-semibold text-on-brand">
                    {activeFilterCount}
                  </span>
                ) : null}
              </Button>
            ) : null}
            {actions}
          </span>
        </div>

        {tabs}

        {hasBar && !compact ? (
          <div className="flex flex-wrap items-center gap-2">
            {filters}
            {tools === undefined ? null : (
              <span className="ml-auto flex items-center gap-2">{tools}</span>
            )}
          </div>
        ) : null}
      </div>

      {/* The body. `grow` so a short list still pushes the footer down to the
          bottom of the pane rather than leaving it floating mid-screen. */}
      <div className="grow">{children}</div>

      {/* ---------------------------------------------------------------- */}
      {/* Footer — frozen to the bottom. The count is always here; the pager  */}
      {/* only when there is more than one page, because a pager that cannot   */}
      {/* page is furniture (X-6.2).                                           */}
      {/*                                                                      */}
      {/* `bottom-0` on mobile is deliberate rather than an oversight: the pane */}
      {/* carries `pb-24` to clear the bottom nav, and a sticky inset resolves  */}
      {/* past that padding — so plain `bottom-0` lands the bar directly ABOVE  */}
      {/* the nav. On sm and up the pane's padding is 16px and the bar goes     */}
      {/* flush, mirroring the header.                                          */}
      {/* ---------------------------------------------------------------- */}
      {showFooter ? (
        <div
          className={clsx(
            'sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3',
            'border-t border-hairline bg-canvas px-1 pt-2 pb-2',
            // Mirror of the header: `bottom-0` is the content box bottom, so the
            // bar never shifts, and the `::after` paints the pane's padding band
            // beneath it. On a phone that band is 96px of clearance for the
            // bottom nav, which is opaque already — hence sm-only.
            "sm:after:absolute sm:after:inset-x-0 sm:after:-bottom-4 sm:after:h-4 sm:after:bg-canvas sm:after:content-['']",
          )}
        >
          <span className="flex flex-wrap items-center gap-3">
            <span className="text-meta text-ink2">
              {total.toLocaleString('en-US')} {noun ?? 'items'}
            </span>
            {footerExtra}
          </span>
          {canPage ? <Pager page={page} pageSize={pageSize} total={total} onPage={onPage} /> : null}
        </div>
      ) : null}

      {/* The mobile filter sheet. The same bottom sheet the More menu uses, so
          "a panel slides up from the bottom" means one thing in this app.
          Mounted only in the compact layout — see `useCompactHeader`. */}
      <Dialog
        open={sheetOpen && compact}
        onOpenChange={setSheetOpen}
        title={`Filter ${title.toLowerCase()}`}
        footer={
          <Button
            variant="primary"
            onClick={() => {
              setSheetOpen(false);
            }}
          >
            Done
          </Button>
        }
      >
        {/* Filters apply as they change — there is no Apply step, because the
            list behind is already URL-driven and a staged filter form would be
            a second source of truth for what is on screen (docs/03 §1). */}
        <div className="flex flex-col gap-4">
          {compact ? filters : null}
          {compact ? tools : null}
        </div>
      </Dialog>
    </div>
  );
}
